
import { computed, defineComponent, nextTick, ref, shallowRef, watch, PropType } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import clamp from 'lodash/clamp'
import SpinnerLoader from '../SpinnerLoader.vue'
export default defineComponent({
    props: {
        focused: Boolean,
        value: { type: null, default: () => [] },
        items: { type: Array, default: () => [] },
        itemValue: { type: String, default: 'id' },
        itemName: { type: String, default: 'name' },
        returnObject: Boolean,
        multiple: Boolean,
        label: { type: String, default: 'Поиск...' },
        disabled: { type: Boolean, default: false },
        openOnClick: { type: Boolean, default: true },
        transition: { type: null, default: 'v-menu-transition' },
        showTooltip: { type: Boolean, default: false },
        menuProps: { type: Object },
        fixedHeight: {type: Object},
        isVars: {type: Boolean, default: false},
        loading: {type: Boolean, default: false},
        withSearch: {type: Boolean, default: true},
        allLabel: {type: String, default: "Все"},
        allLabelBold: {type: Boolean, default: false},
        width: {type: String as PropType<string | undefined>, default: undefined},
        itemGroup: {type: String, default: ''},
        ungroupedLabel: {type: String, default: 'Прочее'},
        sort: {type: Boolean, default: true},
    },
    components: {SpinnerLoader},
    setup(props, { emit }) {
        const V_SCROLL_ITEM_HEIGHT = 48
        const V_SCROLL_DEFAULT_HEIGHT = 288
        const V_SCROLL_GROUP_HEIGHT = 26
        const PINNED_COLLAPSE_LIMIT = 3
        const PINNED_SUMMARY_ROW = { _isPinnedSummary: true }
        const PINNED_TOGGLE_ROW = { _isPinnedToggle: true }
        const searchQuery = ref('')
        const pinnedIds = shallowRef<any[]>([])
        const pinnedExpanded = ref(false)
        const groupedList = ref<HTMLElement | null>(null)
        const virtualScroll = ref<any>(null)

        const getItemLabel = (item: any) => String(item?.[props.itemName] ?? '')
        const compareByLabel = (a: any, b: any) =>
            getItemLabel(a).localeCompare(getItemLabel(b), 'ru', { sensitivity: 'base' })
        const getSearchRank = (label: string, query: string) => {
            const name = label.toLowerCase()
            if (name.startsWith(query)) return 0
            const index = name.indexOf(query)
            if (index === -1) return 3
            if (index + query.length === name.length) return 2
            return 1
        }
        const getCurrentSelectedIds = () => {
            if (props.multiple) {
                return Array.isArray(props.value) ? [...props.value] : []
            }
            if (props.value === null || props.value === undefined || props.value === '') {
                return []
            }
            return [props.value]
        }
        const isAllSelected = computed(() => {
            if (!props.multiple) return false
            const items = props.items || []
            if (!items.length) return false
            return Array.isArray(props.value) && props.value.length === items.length
        })

        const getItemGroup = (item: any) => {
            if (!props.itemGroup) {
                return ''
            }
            const group = item?.[props.itemGroup]
            if (group == null) {
                return ''
            }
            return String(group).trim()
        }

        const showGroups = computed(() =>
            Boolean(props.itemGroup) &&
            (props.items || []).some((el: any) => Boolean(getItemGroup(el)))
        )

        const itemMatchesQuery = (el: any, query: string) => {
            if (getItemLabel(el).toLowerCase().includes(query)) {
                return true
            }
            if (showGroups.value) {
                const group = getItemGroup(el)
                if (group && group.toLowerCase().includes(query)) {
                    return true
                }
            }
            return false
        }

        const sortBySearch = (items: any[], query: string) => {
            return items.slice().sort((a, b) => {
                const rankA = getSearchRank(getItemLabel(a), query)
                const rankB = getSearchRank(getItemLabel(b), query)
                if (rankA !== rankB) return rankA - rankB
                return compareByLabel(a, b)
            })
        }

        const buildGroupedRows = (items: any[], sortGroupNames = false) => {
            if (!showGroups.value) {
                return [...items]
            }
            const groups = new Map<string, any[]>()
            const ungrouped: any[] = []
            items.forEach((item: any) => {
                const group = getItemGroup(item)
                if (group) {
                    if (!groups.has(group)) {
                        groups.set(group, [])
                    }
                    groups.get(group)!.push(item)
                } else {
                    ungrouped.push(item)
                }
            })
            const rows: any[] = []
            const groupNames = [...groups.keys()]
            if (sortGroupNames) {
                groupNames.sort((a, b) => a.localeCompare(b, 'ru', { sensitivity: 'base' }))
            }
            groupNames.forEach((name) => {
                rows.push({ _isGroupHeader: true, name })
                rows.push(...groups.get(name)!)
            })
            if (ungrouped.length) {
                rows.push({ _isGroupHeader: true, name: props.ungroupedLabel })
                rows.push(...ungrouped)
            }
            return rows
        }
        const getRowsHeight = (rows: any[]) =>
            rows.reduce((sum: number, row: any) => {
                return sum + (row._isGroupHeader ? V_SCROLL_GROUP_HEIGHT : V_SCROLL_ITEM_HEIGHT)
            }, 0)

        const pinnedItems = computed(() => {
            if (!props.sort || isAllSelected.value) return []
            const idSet = new Set(pinnedIds.value)
            if (!idSet.size) return []
            return (props.items || [])
                .filter((el: any) => idSet.has(el[props.itemValue]))
                .slice()
                .sort(compareByLabel)
        })
        const visiblePinnedItems = computed(() => {
            const query = searchQuery.value.trim().toLowerCase()
            if (!query) return pinnedItems.value
            return sortBySearch(
                pinnedItems.value.filter((el: any) => itemMatchesQuery(el, query)),
                query
            )
        })
        const canCollapsePinned = computed(() =>
            pinnedItems.value.length > PINNED_COLLAPSE_LIMIT && !isAllSelected.value
        )
        const pinnedRows = computed(() => {
            if (!pinnedItems.value.length) return []
            if (canCollapsePinned.value && !pinnedExpanded.value) {
                return [PINNED_SUMMARY_ROW]
            }
            const items = visiblePinnedItems.value
            if (!items.length) return []
            const rows = buildGroupedRows(items, Boolean(props.sort && !searchQuery.value.trim()))
            if (canCollapsePinned.value) {
                rows.push(PINNED_TOGGLE_ROW)
            } else if (rows.length) {
                rows[rows.length - 1] = { ...rows[rows.length - 1], _isPinnedTail: true }
            }
            return rows
        })
        const pinnedSummaryText = computed(() => {
            const selected = Array.isArray(props.value) ? props.value : []
            const query = searchQuery.value.trim().toLowerCase()
            if (query) {
                const itemsById = new Map((props.items || []).map((el: any) => [el[props.itemValue], el]))
                const matched = selected.filter((id: any) => {
                    const item = itemsById.get(id)
                    return item && itemMatchesQuery(item, query)
                }).length
                return `Выбрано: ${matched}(${selected.length})`
            }
            const pinnedSet = new Set(pinnedIds.value)
            const n = selected.filter((id: any) => pinnedSet.has(id)).length
            const m = selected.filter((id: any) => !pinnedSet.has(id)).length
            return m > 0 ? `Выбрано: ${n}(+${m})` : `Выбрано: ${n}`
        })
        const scrollListToTop = () => {
            if (groupedList.value) {
                groupedList.value.scrollTop = 0
            }
            const scroller = virtualScroll.value
            if (scroller && scroller.$el) {
                scroller.$el.scrollTop = 0
                if (typeof scroller.onScroll === 'function') {
                    scroller.onScroll()
                }
            }
        }
        const expandPinned = () => {
            pinnedExpanded.value = true
        }
        const collapsePinned = () => {
            scrollListToTop()
            pinnedExpanded.value = false
        }
        watch(searchQuery, () => {
            nextTick(() => scrollListToTop())
        })

        const filteredItems = computed(() => {
            const query = searchQuery.value.trim().toLowerCase()
            const pinnedSet = props.sort && !isAllSelected.value ? new Set(pinnedIds.value) : null
            const source = props.items || []
            if (!props.sort && !query) {
                return source
            }
            const items = source.filter((el: any) => {
                if (pinnedSet && pinnedSet.has(el[props.itemValue])) {
                    return false
                }
                if (!query) {
                    return true
                }
                return itemMatchesQuery(el, query)
            })
            if (!props.sort) {
                return items
            }
            if (!query) {
                return items.slice().sort(compareByLabel)
            }
            return sortBySearch(items, query)
        })
        const restGroupedRows = computed(() => {
            const items = filteredItems.value || []
            if (!showGroups.value) {
                return items
            }
            return buildGroupedRows(items, Boolean(props.sort && !searchQuery.value.trim()))
        })
        const groupedRows = computed(() => {
            if (!showGroups.value) {
                return filteredItems.value || []
            }
            return [...pinnedRows.value, ...restGroupedRows.value]
        })
        const virtualScrollItems = computed(() => [...pinnedRows.value, ...(filteredItems.value || [])])
        const hasVisibleItems = computed(() => virtualScrollItems.value.length > 0)
        const vScrollHeight = computed(() =>
            clamp((virtualScrollItems.value?.length || 0) * V_SCROLL_ITEM_HEIGHT, 0, V_SCROLL_DEFAULT_HEIGHT)
        )
        const groupedListHeight = computed(() => {
            const height = getRowsHeight(groupedRows.value)
            return clamp(height, 0, V_SCROLL_DEFAULT_HEIGHT)
        })

        const selected = computed(() => {
              return props.multiple
                ? props.items?.filter((el: any) => props.value.includes(el[props.itemValue]))
                : props.items?.find((el: any) => el[props.itemValue] === props.value)
          }
        )

        const menuOpened = ref(false)
        const selectAllIconColor = 'mainNew'
        const update = (val: any) => {
            emit('input', val)
        }
        watch(
            menuOpened,
            val => {
                if (val && props.sort) {
                    const ids = getCurrentSelectedIds()
                    const items = props.items || []
                    const allSelected = props.multiple && items.length && ids.length === items.length
                    pinnedIds.value = allSelected ? [] : ids
                    pinnedExpanded.value = false
                }
                setTimeout(() => (searchQuery.value = ''), 200)
            },
            { immediate: true }
        )

        const selectedAll = computed(() => isAllSelected.value)

        const selectAllIcon = computed(() => {
            if (selectedAll.value) return 'mdi-checkbox-marked'
            if (props.value.length) return 'mdi-minus-box'
            return 'mdi-checkbox-blank-outline'
        })
        const unpin = (id: any) => {
            if (!props.sort) return
            pinnedIds.value = pinnedIds.value.filter((pinnedId: any) => pinnedId !== id)
        }
        const select = (item: any) => {
            if (!item || item._isGroupHeader || item._isPinnedSummary || item._isPinnedToggle || item.disabled) return
            if (props.multiple) {
                const id = item[props.itemValue]
                const val = !props.value.includes(id)
                if (val) {
                    update(props.value.concat(id))
                } else {
                    unpin(id)
                    update(props.value.filter((el: any) => el !== id))
                }
            } else {
                update(item[props.itemValue])
            }
        }
        const openMenu = () => (menuOpened.value = true)

        const closeMenu = () => (menuOpened.value = false)

        const selectAll = () => {
            if (props.value.length) {
                pinnedIds.value = []
                update([])
            } else {
                update(props.items?.filter((el: any) => !el.disabled).map((el: any) => el[props.itemValue]))
            }
        }
        const activatorId = computed(() => getActivatorId())
        const getActivatorId = () => 'select-activator' + '-' + uuidv4().slice(0, 10)
        const menuWidthStyle = computed(() => {
            const raw = props.width
            let widthPart = 'min-width: 200px; overflow: hidden;'
            if (raw != null && raw !== '') {
                const n = parseInt(String(raw), 10)
                if (!Number.isNaN(n)) {
                    widthPart = `width: ${n}px; min-width: ${n}px; max-width: ${n}px; overflow: hidden;`
                }
            }
            if (props.fixedHeight) {
                return `height: ${props.fixedHeight.parent}; ${widthPart}`
            }
            return widthPart
        })
        watch(selected, () => {
            emit('update-selected', selected.value)
            emit('change')
        })
        return {
            V_SCROLL_ITEM_HEIGHT,
            vScrollHeight,
            update,
            searchQuery,
            menuOpened,
            select,
            selected,
            activatorId,
            selectAll,
            selectAllIcon,
            selectAllIconColor,
            filteredItems,
            virtualScrollItems,
            hasVisibleItems,
            pinnedSummaryText,
            expandPinned,
            collapsePinned,
            groupedList,
            virtualScroll,
            showGroups,
            groupedRows,
            groupedListHeight,
            openMenu,
            closeMenu,
            selectedAll,
            menuWidthStyle,
        }
    },
})
